import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { adminContactInboxPush, adminContactListQuery, wsChannel } from '@xdocs/schemas';
import type { AdminContactListResult, ContactSubmission, ErrorAlert, SubmissionStatus, SubmissionType } from '@xdocs/schemas';
import type { DataTableQuery } from '@xdocs/ui';
import { useWs } from '../../../ws/WsProvider.js';

const DEFAULT_QUERY: DataTableQuery = { page: 1, pageSize: 25, sort: 'createdAt', dir: 'asc' };
export const ADMIN_CONTACT_LIST_QUERY_KEY_PREFIX = 'admin-contact-list' as const;

export interface ContactInboxFilters {
  submissionType?: SubmissionType;
  status?: SubmissionStatus;
}

export interface UseContactInboxListResult {
  rows: readonly ContactSubmission[];
  total: number | null;
  nextCursor: string | null;
  query: DataTableQuery;
  onQueryChange: (next: DataTableQuery) => void;
  onLoadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  loadMoreError: string | undefined;
  onRetry: () => void;
  invalidate: () => void;
  /** True for one render cycle right after a live `submission.created` push
   * lands (F53 AC: "new submissions arrive live over `admin:contact-inbox`") —
   * the page surfaces this as a transient inline banner rather than a toast
   * (ux.md: a background list update is non-blocking, not an interruption). */
  justReceivedLive: boolean;
}

/**
 * Server-side pagination/sort/search over `admin.contact.list` (F53, E4
 * §8.3), oldest-unhandled-first by default (SLA ordering, F53 AC), plus the
 * live `admin:contact-inbox` push (§5.5) that refetches the list —
 * `queryInvalidation.ts`'s skeleton deliberately leaves this channel
 * unmapped ("consumed directly by feature hooks"), so this hook subscribes
 * directly, mirroring `routes/explorer/useExplorerList.ts`.
 *
 * `filters` (submission-type tab, status) are SERVER-side (E6 §5.9: a
 * fetch-all-then-filter table is a review block) — kept as a separate param
 * rather than folded into `DataTableQuery` (which only carries
 * page/pageSize/sort/dir/q) so `<DataTable>` stays generic across resources.
 */
export function useContactInboxList(filters: ContactInboxFilters = {}): UseContactInboxListResult {
  const { wsClient, connectionState } = useWs();
  const queryClient = useQueryClient();
  const [query, setQuery] = useState<DataTableQuery>(DEFAULT_QUERY);
  const [rows, setRows] = useState<ContactSubmission[]>([]);
  const [justReceivedLive, setJustReceivedLive] = useState(false);
  const appendModeRef = useRef(false);
  const enabled = connectionState === 'connected';
  // Destructured primitives (not the `filters` object itself) so a fresh
  // object literal passed by the caller each render doesn't change the
  // query-key identity and cause a refetch loop.
  const queryKey = [ADMIN_CONTACT_LIST_QUERY_KEY_PREFIX, query, filters.submissionType ?? null, filters.status ?? null] as const;

  const result = useQuery<AdminContactListResult, ErrorAlert>({
    queryKey,
    queryFn: () => requestTyped(wsClient, 'admin.contact.list', adminContactListQuery.parse({ ...query, ...filters })),
    enabled,
  });

  // Filter changes reset to page 1 (a fresh replace fetch, never append
  // across a filter boundary) — mirrors `useExplorerList`'s `folderId` reset.
  // Only re-run on filter identity change (plain `.ts` file — `react-hooks/
  // exhaustive-deps` isn't registered outside `.jsx`/`.tsx` in this repo's
  // ESLint config, so no disable directive is needed here).
  useEffect(() => {
    appendModeRef.current = false;
    setQuery((prev) => ({ ...prev, page: 1 }));
  }, [filters.submissionType, filters.status]);

  useEffect(() => {
    if (!result.data) return;
    setRows((prev) => (appendModeRef.current ? [...prev, ...result.data.rows] : result.data.rows));
  }, [result.data]);

  useEffect(() => {
    if (connectionState !== 'connected') return undefined;
    return wsClient.subscribe(wsChannel.adminContactInbox, (envelope) => {
      const parsed = adminContactInboxPush.safeParse(envelope.payload);
      if (!parsed.success) return;
      setJustReceivedLive(true);
      void queryClient.invalidateQueries({ queryKey: [ADMIN_CONTACT_LIST_QUERY_KEY_PREFIX] });
    });
  }, [wsClient, queryClient, connectionState]);

  useEffect(() => {
    if (!justReceivedLive) return undefined;
    const timer = setTimeout(() => setJustReceivedLive(false), 6000);
    return () => clearTimeout(timer);
  }, [justReceivedLive]);

  const onQueryChange = useCallback((next: DataTableQuery) => {
    appendModeRef.current = false;
    setQuery(next);
  }, []);

  const onLoadMore = useCallback(() => {
    appendModeRef.current = true;
    setQuery((q) => ({ ...q, page: q.page + 1 }));
  }, []);

  const onRetry = useCallback(() => void result.refetch(), [result]);
  const invalidate = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: [ADMIN_CONTACT_LIST_QUERY_KEY_PREFIX] }),
    [queryClient],
  );

  const total = result.data?.total ?? null;
  const hasMore = total !== null ? rows.length < total : false;
  const isAppendFetch = appendModeRef.current && result.isFetching;

  return {
    rows,
    total,
    nextCursor: hasMore ? 'more' : null,
    query,
    onQueryChange,
    onLoadMore,
    isLoading: result.isLoading && !appendModeRef.current,
    isLoadingMore: isAppendFetch,
    isError: result.isError,
    loadMoreError: appendModeRef.current && result.isError ? result.error?.message : undefined,
    onRetry,
    invalidate,
    justReceivedLive,
  };
}
